import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatErr } from "@/lib/api";
import { useRecaptcha } from "@/lib/recaptcha";

export default function EnquiryDialog({ open, onOpenChange, product }) {
  const { t } = useTranslation();
  const { execute } = useRecaptcha();
  const [form, setForm] = useState({ name: "", organization: "", phone: "", email: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && product) {
      setForm((f) => ({ ...f, message: `Enquiry regarding: ${product.name}` }));
    }
  }, [open, product]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const recaptcha_token = await execute("enquiry");
      await api.post("/enquiries", {
        ...form,
        recaptcha_token,
        product_id: product?.id,
        product_name: product?.name,
      });
      toast.success(t("enquiry.success"));
      setForm({ name: "", organization: "", phone: "", email: "", message: "", website: "" });
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e) || t("enquiry.error"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>
            {t("enquiry.title")}
          </DialogTitle>
          <DialogDescription>{t("enquiry.subtitle")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>{t("enquiry.name")}</Label>
            <Input required data-testid="enquiry-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("enquiry.org")}</Label>
              <Input data-testid="enquiry-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />
            </div>
            <div>
              <Label>{t("enquiry.phone")}</Label>
              <Input data-testid="enquiry-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>{t("enquiry.email")}</Label>
            <Input required type="email" data-testid="enquiry-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label>{t("enquiry.message")}</Label>
            <Textarea required rows={4} data-testid="enquiry-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                 value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                 style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />
          <Button type="submit" disabled={busy} data-testid="enquiry-submit" className="w-full bg-[#0F2942] text-white hover:bg-[#0A192F]">
            {busy ? "Sending…" : t("enquiry.submit")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
